import React, { useEffect } from 'react';
import { string, number } from 'prop-types';
import classNames from 'classnames/bind';
import {
  params,
  shape,
  fragment,
  customType,
  string as stringType,
  number as numberType,
  useDataModel,
  arrayOf
} from '@thd-nucleus/data-sources';
import { Image } from '@thd-olt-component-react/core-ui';
import {
  Button,
  Typography
} from '@one-thd/sui-atomic-components';
import styles from './story-hero.module.scss';

const cx = classNames.bind(styles);

const StoryHero = ({ componentId, componentClass, componentPosition }) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('contentfulhero.ready');
  }, []);

  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    skip: !(componentId && componentClass),
  });

  const enterpriseContentModel = data?.component?.enterpriseContentModel;

  const {
    cta: ctaProductData,
    eyebrow: eyebrowProductData,
    description: descriptionProductData,
    link: linkProductData,
    previewImage: previewImageProductData,
    title: titleProductData,
  } = data?.component || {};

  const {
    details: detailsProductData,
    id: idProductData,
    url: urlProductData,
  } = data?.component?.image || {};

  let cta = ctaProductData;
  let description = descriptionProductData;
  let details = detailsProductData;
  let eyebrow = eyebrowProductData;
  let id = idProductData;
  let imageUrl = urlProductData;
  let link = linkProductData;
  let previewImage = previewImageProductData;
  let title = titleProductData;

  if (previewImage) {
    let damDownloadedContentImage = previewImage.damDownloadedContent;
    let damContentSelectorImage = previewImage.damContentSelector?.assetData?.[0];
    if (damDownloadedContentImage?.url && damDownloadedContentImage?.details) {
      imageUrl = damDownloadedContentImage?.url;
      details = damDownloadedContentImage?.details;
    } else {
      let imageDimensions = damContentSelectorImage?.selectedImageDimensions;
      if (imageDimensions && damContentSelectorImage?.selectedImageUrl) {
        const isNumeric = (stringNum) => {
          return !Number.isNaN(stringNum) && !Number.isNaN(parseFloat(stringNum));
        };
        // Note: Having to parse through a string to get the dimensions is temporary until a more permanent solution
        // is put in place. The assumption here is that the format of the string will always be 'width/height'.
        let cleansedImageDimensions = imageDimensions.replace(/[^0-9/]/g, '');
        let parsedImageDimensions = cleansedImageDimensions.split('/');
        const imageHeight = parsedImageDimensions[1];
        const imageWidth = parsedImageDimensions[0];
        if (isNumeric(imageHeight) && isNumeric(imageWidth)) {
          imageUrl = damContentSelectorImage?.selectedImageUrl;
          details = {
            height: imageHeight,
            width: imageWidth
          };
        }
      }
    }
  }

  if (enterpriseContentModel) {
    cta = enterpriseContentModel.cta;
    description = enterpriseContentModel.subhead;
    details = enterpriseContentModel.image_lifestyle?.damDownloadedContent?.details;
    eyebrow = enterpriseContentModel.eyebrow_LessThan30Char;
    id = enterpriseContentModel.image_lifestyle?.damDownloadedContent?.id;
    imageUrl = enterpriseContentModel.image_lifestyle?.damDownloadedContent?.url;
    link = enterpriseContentModel.link_cta;
    title = enterpriseContentModel.headline_LessThan50Char;
  }

  return (
    <div
      className={cx('story-hero__card-wrapper')}
      id={`${componentClass}-${componentPosition}-${componentId}`}
      data-component-id={componentId}
      data-component-name={componentClass}
      data-component-position={componentPosition}
    >
      <div className={cx('story-hero__card')}>
        <div className={cx('story-hero__image-container')}>
          {imageUrl && (
            <Image
              stretchy
              src={imageUrl}
              alt={`Image for ${title}`}
              title={`Image for ${title}`}
              height={details.height}
              width={details.width}
              className={cx('story-hero__image')}
              fetchpriority="high"
            />
          )}
        </div>
        <div className={cx('story-hero__info-box')}>
          <div className={cx('story-hero__text-box')}>
            {eyebrow
            && (
              <Typography
                height="tight"
                weight="bold"
                variant="body-base"
                color="brand"
              >{eyebrow}
              </Typography>
            )}
            {title
            && (
              <Typography
                height="none"
                weight="condensed"
                variant="display"
                color="primary"
                uppercase
              >{title}
              </Typography>
            )}
            {description
            && (
              <div className={cx('story-hero__text')}>
                <Typography
                  height="tight"
                  weight="bold"
                  variant="body-lg"
                  color="primary"
                >{description}
                </Typography>
              </div>
            )}
          </div>
          {link && cta && (
            <Button href={link} variant="primary">{cta}</Button>
          )}
        </div>
      </div>
    </div>
  );
};

StoryHero.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  componentId: string.isRequired,
  componentClass: string.isRequired,
  componentPosition: number
};

StoryHero.defaultProps = {
  componentPosition: 1
};

StoryHero.displayName = 'Contentful Story Hero';

StoryHero.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['Hero']).isRequired(),
  }).shape({
    Hero: fragment().shape({
      componentName: stringType(),
      cta: stringType(),
      description: stringType(),
      eyebrow: stringType(),
      id: stringType(),
      image: shape({
        contentType: stringType(),
        details: shape({
          height: numberType(),
          size: numberType(),
          width: numberType(),
        }),
        id: stringType(),
        url: stringType(),
      }),
      previewImage: shape({
        damContentSelector: shape({
          assetData: arrayOf(
            shape({
              selectedImageDimensions: stringType(),
              selectedImageUrl: stringType()
            })
          )
        }),
        damDownloadedContent: shape({
          details: shape({
            height: numberType(),
            width: numberType(),
            size: numberType(),
          }),
          url: stringType()
        }),
      }),
      link: stringType(),
      title: stringType(),
      enterpriseContentModel: shape({
        link_cta: stringType(),
        image_lifestyle: shape({
          damDownloadedContent: shape({
            id: stringType(),
            url: stringType(),
            details: shape({
              height: numberType(),
              width: numberType(),
              size: numberType(),
            }),
          }),
        }),
        headline_LessThan50Char: stringType(),
        subhead: stringType(),
        cta: stringType(),
        eyebrow_LessThan30Char: stringType()
      })
    })
  })
};

export { StoryHero };